import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { hasChanges, profileBody, profileFieldErrors, profileForm, saveProfile, type ProfileForm } from "../../api/subscriberProfile";
import type { SearchAddress, SubscriberMatch } from "../../api/subscriberSearch";
import { ID_PROOF_TYPES } from "./CreateUser";

/**
 * Users › Customer lookup › Edit profile (owner 2026-10-18): everything but
 * the primary mobile. Each field is red with its reason as soon as it is
 * wrong; only what changed is sent.
 */
export function SubscriberProfileDialog({ ns, subscriber, onClose, onSaved }: { ns: string; subscriber: SubscriberMatch | null; onClose: () => void; onSaved: () => void }) {
  const identity = useIdentity();
  const [form, setForm] = useState<ProfileForm | null>(null);
  const save = useMutation({
    mutationFn: () => saveProfile(identity, ns, subscriber!.accountid, profileBody(subscriber!, form!)),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  // A fresh form, and no old error, each time a subscriber is opened.
  const { reset } = save;
  useEffect(() => {
    setForm(subscriber ? profileForm(subscriber) : null);
    reset();
  }, [subscriber, reset]);
  if (!subscriber || !form) return null;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit profile · {subscriber.name || subscriber.mobile}</DialogTitle>
      <DialogContent>
        <ProfileFormView
          m={subscriber}
          form={form}
          onChange={setForm}
          disabled={save.isPending}
          error={save.error ? (save.error instanceof Error ? save.error.message : String(save.error)) : null}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={save.isPending || !hasChanges(profileBody(subscriber, form)) || Object.keys(profileFieldErrors(subscriber, form)).length > 0}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** The form without its hooks, so a test can render it. */
export function ProfileFormView({ m, form, onChange, disabled, error }: { m: SubscriberMatch; form: ProfileForm; onChange: (f: ProfileForm) => void; disabled?: boolean; error?: string | null }) {
  const fe = profileFieldErrors(m, form);
  const err = (k: string) => (fe[k] ? { error: true, helperText: fe[k] } : {});
  const set = <K extends keyof ProfileForm>(k: K, v: ProfileForm[K]) => onChange({ ...form, [k]: v });
  const installs = m.logins.filter((l) => l.installation_id && form.installs[l.installation_id]);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth size="small" label="Mobile (login)" value={m.mobile} disabled helperText="The login: cannot be changed" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth size="small" label="Name (on invoices)" value={form.name} disabled={disabled} onChange={(e) => set("name", e.target.value)} {...err("name")} />
        </Grid>
        <Grid size={{ xs: 12, sm: 2 }}>
          <TextField select fullWidth size="small" label="Gender" value={form.gender} disabled={disabled} onChange={(e) => set("gender", e.target.value as ProfileForm["gender"])}>
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Email"
            value={form.email}
            disabled={disabled}
            onChange={(e) => set("email", e.target.value)}
            helperText="A new email gets a confirm link; login details go only to a confirmed email."
            {...err("email")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Alternate contact number"
            value={form.alternate_mobile}
            disabled={disabled}
            onChange={(e) => set("alternate_mobile", e.target.value)}
            slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 10 } }}
            helperText="10 digits"
            {...err("alternate_mobile")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField select fullWidth size="small" label="ID proof type" value={form.idprooftype || m.idprooftype} disabled={disabled} onChange={(e) => set("idprooftype", e.target.value)} {...err("idprooftype")}>
            <MenuItem value="">None</MenuItem>
            {ID_PROOF_TYPES.map((x) => (
              <MenuItem key={x} value={x}>
                {x}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 8 }}>
          <TextField
            fullWidth
            size="small"
            label="New ID proof number"
            value={form.idproofnumber}
            disabled={disabled}
            onChange={(e) => set("idproofnumber", e.target.value)}
            helperText={m.idproofnumber ? `Recorded: ${m.idproofnumber}. Leave empty to keep it.` : "Optional"}
            {...err("idproofnumber")}
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle2">Billing address</Typography>
      <AddressRow value={form.billing} onChange={(billing) => set("billing", billing)} errors={fe} prefix="billing" disabled={disabled} />

      {installs.map((l) => (
        <Box key={l.installation_id} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="subtitle2">
            Installation · <code>{l.username}</code>
          </Typography>
          <AddressRow
            value={form.installs[l.installation_id!]!}
            onChange={(a) => set("installs", { ...form.installs, [l.installation_id!]: a })}
            errors={fe}
            prefix={`install.${l.installation_id}`}
            disabled={disabled}
          />
        </Box>
      ))}
      {m.logins.some((l) => !l.installation_id) && (
        <Typography variant="caption" color="text.secondary">
          {installs.length === 0 ? "No installation address is recorded for this subscriber's logins." : "A login with no installation recorded has no address to edit here."}
        </Typography>
      )}
      {error && <Alert severity="error">{error}</Alert>}
    </Box>
  );
}

function AddressRow({ value, onChange, errors, prefix, disabled }: { value: SearchAddress; onChange: (a: SearchAddress) => void; errors: Record<string, string>; prefix: string; disabled?: boolean | undefined }) {
  const set = (k: keyof SearchAddress, v: string) => onChange({ ...value, [k]: v });
  const err = (k: keyof SearchAddress) => (errors[`${prefix}.${k}`] ? { error: true, helperText: errors[`${prefix}.${k}`] } : {});
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <TextField fullWidth size="small" label="Address" value={value.address} disabled={disabled} onChange={(e) => set("address", e.target.value)} {...err("address")} />
      </Grid>
      <Grid size={{ xs: 12, sm: 5 }}>
        <TextField fullWidth size="small" label="City" value={value.city} disabled={disabled} onChange={(e) => set("city", e.target.value)} {...err("city")} />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField fullWidth size="small" label="State" value={value.state} disabled={disabled} onChange={(e) => set("state", e.target.value)} {...err("state")} />
      </Grid>
      <Grid size={{ xs: 12, sm: 3 }}>
        <TextField
          fullWidth
          size="small"
          label="PIN"
          value={value.pin}
          disabled={disabled}
          onChange={(e) => set("pin", e.target.value)}
          slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 6 } }}
          {...err("pin")}
        />
      </Grid>
    </Grid>
  );
}
